import type { DocPageContent } from '../docs-content'

const page = {
  order: 5,
  slug: 'media',
  title: '媒体库',
  description:
    '导入媒体、检查文件，并生成代理文件、转录稿、字幕和 AI 场景数据。',
  category: '核心编辑',
  related: ['source-monitor', 'timeline', 'scene-browser', 'export'],
  sections: [
    {
      title: '导入媒体',
      blocks: [
        {
          kind: 'list',
          items: [
            '打开**媒体**标签页并使用**导入**选择文件，或将文件直接拖入素材库。',
            '精剪支持视频、音频、图像、GIF、SVG、Lottie 动画（`.json` 和 `.lottie`）以及生成的素材。GIF 作为图像项导入。',
            '使用**通过 URL 导入媒体**可直接链接到一个媒体文件——该 URL 必须指向文件本身，而不是嵌入该文件的页面。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Apple ProRes 素材的导入、预览和缩略图生成与其他任何片段一样——包括浏览器自身无法解码的格式。导出时，ProRes 片段会被重新编码为你所选的输出编解码器；目前没有 ProRes 输出格式。',
        },
        {
          kind: 'note',
          tone: 'info',
          text: '尚不支持文件夹导入——请将媒体文件直接拖入。能否解码取决于你的浏览器；如果某个文件无法导入，请尝试其他格式或先转码。',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Log 素材（S-Log3 及类似）按设计看起来偏灰、发白——它本就是以这种方式拍摄的。添加 LUT 或调色即可恢复；文件本身没有问题。',
        },
      ],
    },
    {
      title: '检查片段',
      blocks: [
        {
          kind: 'list',
          items: [
            '打开**媒体信息**查看编解码器、尺寸、时长、帧率、文件大小、类型和转录状态。',
            '双击媒体卡片，或使用**在源监视器中打开**，在编辑入时间轴前预览素材。',
            '媒体卡片提供“文件、代理、转录稿、内嵌字幕和 AI 操作”菜单。',
            '素材库变大时，可按类型排序、筛选和分组。',
          ],
        },
      ],
    },
    {
      title: '生成辅助数据',
      blocks: [
        {
          kind: 'table',
          headers: ['操作', '为你提供'],
          rows: [
            [
              '生成代理',
              '为重视频生成的轻量版本，便于更流畅地编辑；导出仍使用原始文件。',
            ],
            ['生成转录稿', '可编辑的语音文本，可搜索并转换为字幕。'],
            [
              '提取内嵌字幕',
              '从文件中提取的字幕轨，可直接插入时间轴。',
            ],
            [
              '用 AI 分析',
              '本地场景检测与字幕生成，使片段可在场景浏览器中被搜索到。',
            ],
          ],
        },
      ],
    },
    {
      title: '复合片段',
      blocks: [
        {
          kind: 'list',
          items: [
            '将时间轴中的一段分组为一个**复合片段**，其行为类似于单个可复用媒体项。',
            '复合片段会出现在媒体库中，并可以像其他素材一样再次放置。',
            '右键点击复合片段并选择**作为标签页打开**，将其作为独立的**序列**进行编辑。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '复合片段可以嵌套——一个可包含另一个——只要链条从不回环自身。',
        },
      ],
    },
    {
      title: 'Lottie 动画',
      blocks: [
        {
          kind: 'paragraph',
          text: '将 Lottie 文件——原始 `.json` 或打包的 `.lottie` 归档——作为时间轴项导入，在预览和导出中以逐帧精确的方式渲染。选中一个 Lottie 片段即可在属性面板的 **Lottie** 部分显示其编辑控件。',
        },
        {
          kind: 'list',
          items: [
            '控制**速度**、**倒放**、**循环**和**乒乓**播放。',
            '设置**起始帧**/**结束帧**片段以仅播放动画的一部分，或选择一个命名的**标记**将片段跳转到该处。',
            '为动画重新着色——作者命名的**颜色**会首先显示，其余形状收纳在展开项之下；共享颜色会编辑所有使用了它的形状，而**全部重置**会恢复原始状态。',
            '就地编辑模板的**文本**图层，并在**属性**下调整已暴露的标量值与矢量值槽位。',
            '对于打包了多个动画的 `.lottie` 归档，使用**动画**选择器在它们之间切换；当文件附带主题时，使用**主题**选择器切换调色板。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '颜色、文本、槽位和片段的编辑会在你拖动或输入时在画布上实时预览，并作为单个撤销步骤提交。动画、主题、标记、片段和模板编辑每次仅作用于一个选中的 Lottie 片段。',
        },
      ],
    },
    {
      title: '修复缺失媒体',
      blocks: [
        {
          kind: 'note',
          tone: 'warning',
          text: '当链接的文件被移动、重命名、删除或需要更新权限时，会出现“缺失媒体”。使用**授予访问权限**、**定位**、**定位文件夹**或**浏览其他文件夹**来恢复链接。仅在打算稍后重新链接时才使用**离线工作**。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
